import React, { useEffect } from 'react'
import { useI18n } from '@ggs/i18n'
import { useModal } from '@ggs/ui/common'
import { get, noop } from 'lodash'
import { SaveProjectResourceForm } from '../../index'
import { useResolveComponentData } from '../../../local'
import { Box } from '@mui/material'
import { WishlistButton } from '../../buttons'

import './AddProjectResourceAction.scss'

/**
 * @typedef {Object} AddProjectResourceActionProps
 * @property {string=} defaultResourceType
 * @property {string=} defaultResourceId
 */

/**
 * Add project resource action button.
 * @param {AddProjectResourceActionProps} props
 * @return {JSX.Element}
 */
export const AddProjectResourceAction = ({ defaultResourceType, defaultResourceId }) => {
  const { t } = useI18n()
  const { jsElementRef, jsComponentData } = useResolveComponentData({
    containerType: 'resource',
    attributeNames: ['data-resource-type', 'data-resource-id', 'data-action-label', 'data-style'],
  })
  const finalResourceType = get(jsComponentData, 'data-resource-type', defaultResourceType)
  const finalResourceId = get(jsComponentData, 'data-resource-id', defaultResourceId)
  const actionLabel = get(jsComponentData, 'data-action-label', t('Save Resource to Project'))
  const style = get(jsComponentData, 'data-style', 'dark')
  const userId = get(window || {}, 'drupalSettings.user.uid', 1)

  const { modal, triggerModal } = useModal({
    className: 'save-project-dialog',
    scroll: 'body',
    title: (
      <Box className="save-project-dialog__title">
        <h2>{t('Save Resource to Project')}</h2>
      </Box>
    ),
    content: (props) => {
      const closeModal = get(props, 'closeModal', noop)
      return (
        <SaveProjectResourceForm
          resourceType={finalResourceType}
          resourceId={finalResourceId}
          closeModal={closeModal}
        />
      )
    },
  })

  useEffect(() => {
    // Check if the URL contains the "triggered_resource={id}" query parameter
    const urlParams = new URLSearchParams(window.location.search)
    const resourceArg = urlParams.get('triggered_resource')
    if (userId !== 0 && resourceArg && finalResourceId === resourceArg) {
      // Trigger the modal function
      triggerModal()
      // Remove only the "triggered_resource={id}" parameter from the URL
      const pattern = new RegExp(`triggered_resource=${resourceArg}([&]?)`)
      let urlWithoutResourceArg = window.location.pathname + window.location.search.replace(pattern, '')
      if (urlWithoutResourceArg.includes('?') && (urlWithoutResourceArg.endsWith('?') || urlWithoutResourceArg.endsWith('&'))) {
        urlWithoutResourceArg = urlWithoutResourceArg.slice(0, -1)
      }
      window.history.replaceState({}, document.title, urlWithoutResourceArg)
    }
  }, finalResourceId)

  // Skip rendering button for anonymous users.
  if (userId === 0) {
    return null
  }

  return (
    <Box ref={jsElementRef}>
      <WishlistButton onClick={triggerModal} aria-label={actionLabel} label={actionLabel} style={style} />
      {modal}
    </Box>
  )
}
